import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PermissionService } from '../_services';

/**
 * JS-1920 follow-up (2026-11-03): gates /self-service/employees (dashboard listing)
 * and /self-service/employees/:id (per-employee profile) for client users.
 *
 * Agency users pass through — they reach these screens from /self-service URLs but the
 * perm is a client-settings-only toggle, so the backend returns true for them anyway.
 * Clients without the "Client View Employee Profile" perm are redirected to /client-dashboard —
 * the universal client home, same fallback every other client-side guard uses
 * (SelfServiceGuard, ClientLocationsGuard, EditTemplatesGuard, RedirectingGuard). We
 * deliberately avoid /self-service/projects as a fallback because the user may lack the
 * View Projects perm there too, and we'd end up with a blank or further-redirected page.
 *
 * The filter/picker surfaces inside project-planning are NOT routed through this guard,
 * so clients without the perm can still see candidates and use them on self-service
 * projects — matches the backend policy on PermissionController + ClientSelfServiceController.
 */
@Injectable({ providedIn: 'root' })
export class ViewEmployeeProfileGuard implements CanActivate {
  constructor(private router: Router, private permissionService: PermissionService) {}

  canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    if (!currentUser) {
      return of(this.router.createUrlTree(['/login']));
    }

    // Agency users (no client_id on the session) are never gated by this perm.
    if (!currentUser.client_id) {
      return of(true);
    }

    const fallback = this.router.createUrlTree(['/client-dashboard']) as UrlTree;

    // JS-1920 follow-up (2026-11-03): perm name is "Client View Employee Profile" so it
    // reads distinctly from the agency-side "View All Employee Profiles" on the perms
    // matrix. Earlier migrations seeded this row under "View Employee Profile"; a rename
    // migration (2026_11_03_120200) updates it in-place.
    return this.permissionService.canAccessFunctionality('Client View Employee Profile').pipe(
      map((allowed: boolean) => (allowed ? true : fallback)),
      catchError(() => of(fallback))
    );
  }
}
